import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import LoadingBar from '@/global/components/ui/loading-bar'
import ContentGuide from '@/global/guides/ContentGuide'
import { useStore } from '@/store'
import { useEffect, useMemo, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import DigitacaoErrorOverlay from '../ErrorOverlay'
import { filterJornada } from '../utils/filterJornada'
import { useDigitacaoStore } from '../utils/store'
import {
    getDigitacaoRowClassName,
    getDigitacaoRowHeight,
} from './digitacao-getters'
import { useDigitacaoContext } from './useDigitacaoContext'
import { useUpdateDigitacaoQueryData } from './useDigitacaoData'

interface IProps {
    columns: GridColDefPro[]
    isLoadingDiario: boolean
    isLoadingMensal: boolean
    isErrorMensal: boolean
    isErrorDiario: boolean
    diarioError: any
    mensalError: any
    dataMensal?: any[]
    dataDiario?: any[]
    Toolbar: () => React.ReactNode
}

export function DigitacaoSubPages({
    columns,
    dataMensal: mensal = [],
    dataDiario: diario = [],
    diarioError,
    isErrorDiario,
    isErrorMensal,
    isLoadingDiario,
    isLoadingMensal,
    mensalError,
    Toolbar,
}: IProps) {
    const { hash } = useLocation()
    const hasScrolledRef = useRef(false)
    const {
        view,
        periodo: [dataInicial, dataFinal],
        queryParams,
    } = useDigitacaoContext()
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const setFuncionarioId = useStore(state => state.setEmployeeId)
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const isHorarioNoturno = useStore(
        state => state.employee?.horarioNoturno === 'S',
    )
    const organizacao = useDigitacaoStore(state => state.organizacao)

    const isLoading =
        (view == 'mensal' && isLoadingMensal) ||
        (view == 'diario' && isLoadingDiario)

    const MemoizedLoading = useMemo(
        () =>
            isLoading ? (
                <LoadingBar key="loading" className="border-x h-1" />
            ) : (
                <div className="border-x h-1" key="not-loading" />
            ),
        [isLoading],
    )

    const data = useMemo(() => {
        if ([funcionarioId, empresaId].includes(-1)) return []

        if (view === 'mensal') {
            if (!mensal.length) return []
            return mensal
                .filter(
                    view === 'mensal' && organizacao === 'jornada'
                        ? () => true
                        : filterJornada(isHorarioNoturno),
                )
                .map((registro, index) => ({
                    ...registro,
                    id: index + 1,
                }))
        }

        if (!diario.length) return []
        return diario
            .sort((a, b) => a.nome.localeCompare(b.nome))
            .map(({ id, ...rest }, index) => ({
                id: index + 1,
                funcionarioId: id,
                ...rest,
            }))
    }, [mensal, diario, funcionarioId, view, setQueryData])

    useEffect(() => {
        if (!hash || hasScrolledRef.current || isLoading || !data.length) return
        if (view !== 'mensal') return
        if (!/^#\d{4}-\d{2}-\d{2}$/.test(hash)) return

        const dia = hash.replace('#', '')
        const rowIndex = data.findIndex((row: any) => row.dia === dia)
        if (rowIndex === -1) return

        requestAnimationFrame(() => {
            const row = document.querySelector(`.row-dia-${dia}`)
            if (row) {
                row.scrollIntoView({ behavior: 'smooth', block: 'center' })
                hasScrolledRef.current = true
                history.replaceState(
                    '',
                    document.title,
                    window.location.pathname + window.location.search,
                )
                row.classList.add('highlight-row')
                setTimeout(() => {
                    row.classList.remove('highlight-row')
                }, 2000)
            }
        })
    }, [hash, isLoading, data, view])

    const getRowClassNameWithDia = useMemo(() => {
        return (params: { row: any }) => {
            const baseClass = getDigitacaoRowClassName(params)
            const diaClass = params.row.dia ? `row-dia-${params.row.dia}` : ''
            return [baseClass, diaClass].filter(Boolean).join(' ')
        }
    }, [])

    const error = useMemo(() => {
        if (isErrorDiario && view === 'diario')
            return {
                isError: true,
                message: diarioError?.message,
            }
        if (isErrorMensal && view === 'mensal')
            return {
                isError: true,
                message: mensalError?.message,
            }
        if (!isLoading && !data.length)
            return {
                isError: true,
                message: 'Nenhum registro encontrado',
            }
        return {
            isError: false,
            message: '',
        }
    }, [isErrorMensal, isErrorDiario, isLoading, data.length])

    return [
        <Outlet
            key="outlet"
            context={{
                data,
                view,
                periodo: [dataInicial, dataFinal],
                queryParams,
            }}
        />,
        MemoizedLoading,
        <ContentGuide.DataGrid
            key={view}
            loading={!data?.length && isLoading}
            rows={data}
            getRowHeight={getDigitacaoRowHeight}
            columns={columns}
            hideFooter
            components={{
                ErrorOverlay: () => (
                    <DigitacaoErrorOverlay message={error.message} />
                ),
            }}
            slots={{ Toolbar }}
            onRowClick={value => {
                if (view === 'diario' && 'funcionarioId' in value.row)
                    setFuncionarioId(value.row.funcionarioId)
            }}
            getRowClassName={getRowClassNameWithDia}
            error={error.isError}
            pageSize={-1}
        />,
    ]
}
